import { useEffect, useState } from "react";

/**
 * Scroll-spy for a page's "On this page" menu: returns the id of the section
 * nearest the top of the screen. On a phone the menu is a sticky tab bar
 * (page-tabs.css), so a section counts as current from just under the bar
 * (where a tapped tab lands it), and the active tab is scrolled into view
 * within its row — the row only, never the page.
 *
 * `ids` is the page's section ids in order; ids with no element on the page
 * are skipped.
 */
export function useSectionTabs(ids: string[], initial: string = ids[0] ?? ""): string {
  const [activeId, setActiveId] = useState(initial);
  const key = ids.join("|");

  useEffect(() => {
    const els = key
      .split("|")
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    if (els.length === 0) return;
    // The detection band starts where a tapped tab lands its heading. On a
    // phone that is just under the sticky tab bar, wherever the page puts it
    // (its resolved `top`, plus the bar's height); elsewhere a fixed 140px.
    const phone = window.matchMedia?.("(max-width: 768px)").matches === true;
    const bar = document.querySelector<HTMLElement>(".page-tabs .page-side");
    const barTop = phone && bar ? parseFloat(getComputedStyle(bar).top) : NaN;
    const topMargin = Number.isFinite(barTop) ? barTop + 56 : phone ? 200 : 140;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        setActiveId(visible[0].target.id);
      },
      { rootMargin: `-${topMargin}px 0px -55% 0px`, threshold: 0 },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [key]);

  useEffect(() => {
    const row = document.querySelector<HTMLElement>(".page-tabs .page-side ul");
    const tab = row?.querySelector<HTMLElement>("a.active");
    if (!row || !tab || row.scrollWidth <= row.clientWidth) return;
    row.scrollTo({ left: tab.offsetLeft - (row.clientWidth - tab.offsetWidth) / 2, behavior: "smooth" });
  }, [activeId]);

  return activeId;
}
